import {
  Box,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { IconChevronLeft } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ImageInput from "../../components/forms/ImageInput";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { ProfileSkeleton } from "../../components/ui/SkeletonLoader";
import Text from "../../components/ui/Text";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import apiService from "../../services/api";
import { validateImage } from "../../utils/imageUpload";

const EditProfile = () => {
  const navigate = useNavigate();
  const { currentUser, fetchUserProfile } = useAuth();

  const { showToast } = useToast();

  const [isLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: currentUser?.first_name || "",
    lastName: currentUser?.last_name || "",
    bio: currentUser?.bio || "",
  });
  const [initialUserData, setInitialUserData] = useState({
    firstName: currentUser?.first_name || "",
    lastName: currentUser?.last_name || "",
    bio: currentUser?.bio || "",
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Check for unsaved changes
  useEffect(() => {
    const hasChanges =
      formData.firstName !== initialUserData.firstName ||
      formData.lastName !== initialUserData.lastName ||
      formData.bio !== initialUserData.bio;
    setHasUnsavedChanges(hasChanges);
  }, [formData, initialUserData]);

  // Show skeleton loader while loading
  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ mt: "70px", pb: 4 }}>
        <ProfileSkeleton />
      </Container>
    );
  }

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      navigate("/my-profile");
    }
  };

  const handleDiscardChanges = () => {
    setFormData(initialUserData);
    setShowUnsavedDialog(false);
    navigate("/my-profile");
  };

  const handleContinueEditing = () => {
    setShowUnsavedDialog(false);
  };

  // Handle image change from ImageInput
  const handleImageChange = async (file) => {
    if (!file) {
      return;
    }

    // Validate the image
    const validation = validateImage(file);
    if (!validation.isValid) {
      showToast(validation.error, "error");
      return;
    }

    try {
      const response = await apiService.uploadProfileImage(file);
      if (response.success) {
        await fetchUserProfile(currentUser?.accessToken);
        showToast(response.message, "success");
      }
    } catch (error) {
      showToast(error.error, "error");
    }
  };

  // Handle image removal
  const handleRemoveImage = async () => {
    try {
      const response = await apiService.uploadProfileImage(null);
      if (response.success) {
        showToast(response.message, "success");
      }
    } catch (error) {
      showToast(error.error, "error");
    }
  };

  const handleSaveChanges = async () => {
    if (!hasUnsavedChanges) return;

    try {
      setIsSaving(true);

      const profileData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        bio: formData.bio,
        profile_setup_completed: true,
      };
      // Update initial data to current
      setInitialUserData(profileData);
      setHasUnsavedChanges(false);

      // Save to backend
      await apiService.updateUserProfile(profileData);
      await fetchUserProfile(currentUser?.accessToken);

      showToast("Profile updated successfully!", "success");
      navigate("/my-profile");
    } catch (error) {
      showToast("Failed to save profile. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Responsive Back Button and Header */}
      <Box sx={{ mb: 3, display: { xs: "block", md: "none" } }}>
        <Button variant="back" onClick={handleBack} />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <Box
          onClick={handleBack}
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            cursor: "pointer",
            color: "#1890FF",
            "&:hover": {
              color: "#40A9FF",
            },
          }}
        >
          <IconChevronLeft size={20} />
        </Box>
        <Text variant="pageHeader">Edit Profile</Text>
      </Box>

      {/* Content Card */}
      <Box
        sx={{
          borderRadius: 2,
          p: { xs: 0, md: 3 },
          border: { xs: "none", md: "1px solid #E5E5E5" },
          backgroundColor: { xs: "transparent", md: "white" },
        }}
      >
        {/* Profile Image Section */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: "40px" }}>
          <ImageInput
            firstName={formData.firstName}
            lastName={formData.lastName}
            value={currentUser?.profile_image_url}
            onChange={handleImageChange}
            size={120}
            // showRemoveButton={!!currentUser?.profile_image_url}
            onRemove={handleRemoveImage}
          />
        </Box>

        {/* Form Fields */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            mb: "40px",
          }}
        >
          {/* Name Fields - Side by side on desktop */}
          <Box
            sx={{
              display: "flex",
              gap: "16px",
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Input
                label="First name"
                value={formData.firstName}
                onChange={handleInputChange("firstName")}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Input
                label="Last name"
                value={formData.lastName}
                onChange={handleInputChange("lastName")}
              />
            </Box>
          </Box>

          {/* Bio Field - Full width */}
          <Input
            label="Bio"
            value={formData.bio}
            onChange={(e) => {
              if (e.target.value.length > 500) return;
              handleInputChange("bio")(e);
            }}
            multiline
            rows={3}
          />
        </Box>

        {/* Save Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="primary"
            onClick={handleSaveChanges}
            disabled={!hasUnsavedChanges || isSaving}
            sx={{
              minWidth: "160px",
              "&.Mui-disabled": {
                color: "#A1A1A1 !important",
                backgroundColor: "#F5F5F5 !important",
                border: "1px solid #D4D4D4",
              },
              width: { xs: "100%", md: "auto" },
            }}
          >
            {isSaving ? "Saving..." : "Save changes"}
          </Button>
        </Box>
      </Box>
      {/* Unsaved Changes Dialog */}
      <Dialog
        open={showUnsavedDialog}
        onClose={handleContinueEditing}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { borderRadius: "12px", m: 2, width: "310px" } },
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 1 }}>
          <Text variant="pageHeader" sx={{ fontSize: "20px" }}>
            Unsaved changes
          </Text>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Text variant="paragraph">
            You have unsaved changes do you want to discard?
          </Text>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            pt: 1,
            gap: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Button
            variant="primary"
            onClick={handleContinueEditing}
            sx={{ width: "100%" }}
          >
            Continue editing
          </Button>
          <Button
            variant="secondary"
            onClick={handleDiscardChanges}
            sx={{ width: "100%" }}
          >
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditProfile;
